import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AdminPlatformAppDeployment,
  PlatformAppComponentKind,
  PlatformAppControls,
  PlatformAppDeployment,
  PlatformAppOperation,
  PlatformAppState,
} from '@traceio-org/platform-contracts';
import type { RecordStatusTone } from '@traceio-org/ui';
import { classifyAdminFailure, isAbortError, type AdminFailure } from './api';

/**
 * Words and behaviour of the Apps pages. Each control is named for what it does on App Platform (see platform-apps.ts
 * in the contracts), never for what a reader might hope it does: Trace's apps run images built by GitHub Actions, so
 * nothing here ships new code.
 */

export const APP_STATE: Readonly<Record<PlatformAppState, { label: string; tone: RecordStatusTone }>> = {
  running: { label: 'Running', tone: 'positive' },
  deploying: { label: 'Deploying', tone: 'info' },
  archived: { label: 'Stopped (archived)', tone: 'neutral' },
  maintenance: { label: 'Maintenance mode', tone: 'warning' },
  not_deployed: { label: 'Not deployed', tone: 'warning' },
  unknown: { label: 'Unknown', tone: 'neutral' },
};

export const COMPONENT_KINDS: Readonly<Record<PlatformAppComponentKind, string>> = {
  service: 'Service',
  worker: 'Worker',
  job: 'Job',
  static_site: 'Static site',
  function: 'Functions',
};

/** What each control does, in the words its button and dialog use. */
export const OPERATIONS: Readonly<
  Record<
    PlatformAppOperation,
    { label: string; title: (app: string) => string; submit: string; what: string }
  >
> = {
  restart: {
    label: 'Restart',
    title: (app) => `Restart ${app}?`,
    submit: 'Restart',
    what: 'Restarts the running containers one at a time on the version they run now. Nothing is rebuilt, no new code is deployed, and jobs (migrations) do not run.',
  },
  redeploy: {
    label: 'Redeploy',
    title: (app) => `Redeploy ${app}?`,
    submit: 'Redeploy',
    what: 'Deploys the same version and settings again, as a new deployment: the same images, and the pre-deploy jobs (migrations) run again. It does not pick up new code; GitHub deploys do that.',
  },
};

/**
 * Why a control cannot be used now, in words for its button's description; null when it can. Checked in this order:
 * the environment (no control token), the person (not a platform admin), then the app (a deployment under way, or a
 * state the control does not apply to). platform-api checks the same again and stays the authority.
 */
export function controlBlocked(
  operation: PlatformAppOperation,
  context: {
    controls: PlatformAppControls;
    canOperate: boolean;
    state: PlatformAppState;
    deploying: boolean;
  },
): string | null {
  if (!context.controls.available) {
    return context.controls.reason === 'no_control_token'
      ? 'Controls are off in this environment: platform-api has no DigitalOcean control token.'
      : 'Controls are off: Apps is not connected to DigitalOcean here.';
  }
  if (!context.canOperate) return 'Only platform admins can restart and redeploy apps.';
  if (context.deploying) return 'A deployment is under way. Wait for it to finish.';
  if (context.state === 'unknown') return "DigitalOcean did not return this app's state.";
  if (context.state === 'archived') {
    return 'The app is stopped (archived) in DigitalOcean. Start it there first.';
  }
  return null;
}

/** A deployment's phase in words (Apps and Health's Recent activity). */
export const DEPLOYMENT_PHASES: Readonly<Record<PlatformAppDeployment['phase'], string>> = {
  UNKNOWN: 'Unknown',
  PENDING_BUILD: 'Waiting to build',
  BUILDING: 'Building',
  PENDING_DEPLOY: 'Waiting to deploy',
  DEPLOYING: 'Deploying',
  ACTIVE: 'Live',
  SUPERSEDED: 'Replaced',
  ERROR: 'Failed',
  CANCELED: 'Canceled',
};

/** How a deployment is going, in words: its phase and its steps. */
export function deploymentWords(deployment: PlatformAppDeployment): string {
  const progress = deployment.progress;
  const steps =
    progress && deployment.outcome === 'in_progress' ? `, ${progress.done} of ${progress.total} steps` : '';
  return `${DEPLOYMENT_PHASES[deployment.phase]}${steps}`;
}

/** How often a page following a deployment reads it (platform-api shares each read for 5 seconds). */
export const FOLLOW_INTERVAL_MS = 10_000;
/** A deployment followed this long without ending is left to Check again (App Platform's own limit is far below). */
export const FOLLOW_MAX_MS = 30 * 60_000;
/** Reads in a row that may fail before following stops (each failure is shown). */
const FOLLOW_MAX_FAILURES = 3;

export interface DeploymentFollow {
  /** The deployment as last read (the one given until the first read answers). */
  deployment: PlatformAppDeployment | null;
  /** Still reading it every FOLLOW_INTERVAL_MS. */
  following: boolean;
  /** The last read failed (following continues until FOLLOW_MAX_FAILURES in a row). */
  failure: AdminFailure | null;
  /** Following stopped before the deployment ended (too long, or reads kept failing). */
  gaveUp: boolean;
}

/**
 * Follows one deployment until it ends: reads it every FOLLOW_INTERVAL_MS while it runs and the tab is visible (only
 * that deployment, never the whole app or the list), then calls onEnd once so the page reads the app again. A new
 * deployment (another operation) starts a new follow.
 */
export function useDeploymentFollow(
  start: PlatformAppDeployment | null,
  read: (deploymentId: string, signal: AbortSignal) => Promise<AdminPlatformAppDeployment>,
  onEnd: (deployment: PlatformAppDeployment) => void,
  options: { intervalMs?: number; maxMs?: number } = {},
): DeploymentFollow {
  const intervalMs = options.intervalMs ?? FOLLOW_INTERVAL_MS;
  const maxMs = options.maxMs ?? FOLLOW_MAX_MS;
  const [state, setState] = useState<DeploymentFollow>({
    deployment: start,
    following: start?.outcome === 'in_progress',
    failure: null,
    gaveUp: false,
  });
  const readRef = useRef(read);
  readRef.current = read;
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  const startId = start?.id ?? null;

  const end = useCallback((deployment: PlatformAppDeployment) => {
    setState({ deployment, following: false, failure: null, gaveUp: false });
    onEndRef.current(deployment);
  }, []);

  useEffect(() => {
    setState({
      deployment: start,
      following: start?.outcome === 'in_progress',
      failure: null,
      gaveUp: false,
    });
    if (!start || start.outcome !== 'in_progress') return;
    const began = Date.now();
    let failures = 0;
    let stopped = false;
    let controller: AbortController | null = null;
    const tick = () => {
      if (stopped) return;
      if (Date.now() - began > maxMs) {
        stopped = true;
        controller?.abort();
        setState((now) => ({ ...now, following: false, gaveUp: true }));
        return;
      }
      // One read at a time, and none while the tab is hidden (it resumes on the next tick once visible).
      if (controller || document.visibilityState === 'hidden') return;
      const current = new AbortController();
      controller = current;
      readRef.current(start.id, current.signal).then(
        (answer) => {
          controller = null;
          if (stopped || current.signal.aborted) return;
          failures = 0;
          if (answer.deployment.outcome !== 'in_progress') {
            stopped = true;
            end(answer.deployment);
          } else {
            setState({ deployment: answer.deployment, following: true, failure: null, gaveUp: false });
          }
        },
        (error: unknown) => {
          controller = null;
          if (stopped || current.signal.aborted || isAbortError(error)) return;
          failures += 1;
          const failure = classifyAdminFailure(error);
          if (failures >= FOLLOW_MAX_FAILURES) {
            stopped = true;
            setState((now) => ({ ...now, following: false, failure, gaveUp: true }));
          } else {
            setState((now) => ({ ...now, failure }));
          }
        },
      );
    };
    const timer = window.setInterval(tick, intervalMs);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      controller?.abort();
    };
    // A new deployment id starts a new follow; the same one given again (a re-render) does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startId, intervalMs, maxMs, end]);

  return state;
}
